import { Button, CloseButton, Dialog, Portal } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { t } from 'i18next';
import { useState } from 'react';

import { ApiError, PillarsService } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface DeletePillarModalProps {
  pillarId: string;
  pillarName: string;
  onDelete?: (id: string) => void;
  onCancel?: () => void;
  children: React.ReactNode;
}

export const PillarDeleteDialog = ({ pillarId, pillarName, onDelete, onCancel, children }: DeletePillarModalProps) => {
  const { slug } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const showToast = useCustomToast();
  const [open, setOpen] = useState(false);

  const mutationPillar = useMutation({
    mutationFn: async () =>
      await PillarsService.deletePillar({
        companySlug: slug,
        id: pillarId,
      }),
    onSuccess: async () => {
      showToast('Succès', 'Pilier supprimer avec succès', 'success');
      await queryClient.invalidateQueries({ queryKey: ['csrPlans'] });
      setOpen(false);
      onDelete && onDelete(pillarId);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
  });

  const handleDelete = () => {
    mutationPillar.mutate();
  };

  return (
    <Dialog.Root role="alertdialog" lazyMount open={open} onOpenChange={(e) => setOpen(e.open)}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>

      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Supprimer "{pillarName}" ?</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              <p>
                Cette action est irréversible. Le pilier <strong>{pillarName}</strong> sera définitivement supprimé du
                plan RSE, ainsi que toutes ses données associées.
              </p>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild onClick={() => onCancel && onCancel()}>
                <Button variant="outline">{t('ui.cancel')}</Button>
              </Dialog.ActionTrigger>

              <Button bg="red.500" onClick={handleDelete}>
                {t('ui.delete')}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
